import React, { useCallback, useEffect, useMemo } from 'react'
import { Table } from 'antd'
import { isNil, some } from 'lodash'

import { showErrorNotification } from '../../../core/helpers'

import { PAGINATION } from '../../../common/constants'

import {
  useMenuCurationTasksFetchParams,
  useMenuCurationTasksListFilters,
  useMenuCurationTasksQuery,
} from '../../hooks'
import { createMenuCurationTaskTableColumns } from '../../services'

import MenuCurationTaskEditableRow from './menu-curation-task-editable-row'

const MenuCurationTasksTable = () => {
  const { setFilters } = useMenuCurationTasksListFilters()

  const { filters, sorters, pagination } = useMenuCurationTasksFetchParams()

  const { data, isLoading, error } = useMenuCurationTasksQuery(
    filters,
    sorters,
    pagination,
  )

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'Failed to get menu curation task',
        description: error,
      })
    }
  }, [error])

  const { result, total } = data || {}

  const isDeleteButtonVisible = useMemo(
    () => some(result, task => task.menu?.isFake),
    [result],
  )

  const tableColumns = useMemo(
    () =>
      createMenuCurationTaskTableColumns(isDeleteButtonVisible, sorters.sortBy),
    [isDeleteButtonVisible, sorters.sortBy],
  )

  const components = useMemo(
    () => ({
      body: {
        row: MenuCurationTaskEditableRow,
      },
    }),
    [],
  )

  const handleChangePagination = useCallback(
    (page, size) =>
      setFilters({
        currentPage:
          size !== pagination.pageSize ? PAGINATION.DEFAULT_PAGE : page,
        pageSize: size,
      }),
    [pagination.pageSize, setFilters],
  )

  const paginationConfig = useMemo(() => {
    if (isNil(total)) {
      return false
    }

    return {
      current: Number(pagination.currentPage),
      pageSize: pagination.pageSize,
      total,
      onChange: handleChangePagination,
      onShowSizeChange: handleChangePagination,
      pageSizeOptions: PAGINATION.DEFAULT_PAGINATION_SIZE_OPTIONS,
    }
  }, [
    total,
    pagination.currentPage,
    pagination.pageSize,
    handleChangePagination,
  ])

  return (
    <Table
      loading={isLoading}
      components={components}
      rowKey="id"
      columns={tableColumns}
      dataSource={result}
      pagination={paginationConfig}
    />
  )
}

export default MenuCurationTasksTable
